import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  AppBar,
  Toolbar,
  Divider,
  ImageList,
  ImageListItem,
} from '@mui/material';
import {
  ArrowBack,
  LocationOn,
  Schedule,
  CheckCircle,
  CameraAlt,
  Phone,
  DirectionsCar,
  Assignment,
  BrokenImage,
  SkipNext,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { ServiceRequest, JobCompletion } from '../types';

// Photo component with loading and error handling
const PhotoItem: React.FC<{ url: string; alt: string }> = ({ url, alt }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [imageSrc, setImageSrc] = useState(url);

  const handleLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleError = () => {
    setLoading(false);
    if (retryCount < 2) {
      // Retry loading the image after a short delay
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setError(false);
        setLoading(true);
        // Force reload by adding a cache-busting parameter
        setImageSrc(`${url}?retry=${retryCount + 1}&t=${Date.now()}`);
      }, 1000 * (retryCount + 1)); // Exponential backoff: 1s, 2s
    } else {
      setError(true);
    }
  };

  // Reset when URL changes
  useEffect(() => {
    setLoading(true);
    setError(false);
    setRetryCount(0);
    setImageSrc(url);
  }, [url]);

  return (
    <ImageListItem
      sx={{
        position: 'relative',
        aspectRatio: '1',
        overflow: 'hidden',
        borderRadius: 1,
        bgcolor: 'grey.200',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {loading && !error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.200',
            zIndex: 1,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      {error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.200',
            color: 'text.secondary',
            p: 1,
            zIndex: 1,
          }}
        >
          <BrokenImage sx={{ fontSize: 32, mb: 0.5 }} />
          <Typography variant="caption" align="center">
            Failed to load
          </Typography>
        </Box>
      )}
      <img
        src={imageSrc}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: loading || error ? 0 : 1,
          display: 'block',
        }}
      />
    </ImageListItem>
  );
};

const CrewJobDetailsPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [job, setJob] = useState<any>(null);
  const [completion, setCompletion] = useState<JobCompletion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Dialogs
  const [checkInDialog, setCheckInDialog] = useState(false);
  const [completeDialog, setCompleteDialog] = useState(false);
  const [photoDialog, setPhotoDialog] = useState(false);
  
  // Form data
  const [crewSize, setCrewSize] = useState(1);
  const [notes, setNotes] = useState('');
  const [issues, setIssues] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [photoType, setPhotoType] = useState<'before' | 'after'>('before');

  useEffect(() => {
    if (user?.userType !== 'crew') {
      navigate('/login');
      return;
    }
    fetchJobDetails();
  }, [jobId, user, navigate]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/crew/jobs/today`);
      if (response.success && response.data) {
        const jobData = (response.data as any).find((j: any) => j.id === jobId || j._id === jobId);
        if (jobData) {
          setJob(jobData);
          // Merge photos from jobData into completion object for consistent access
          // Backend returns photos at jobData.beforePhotos/afterPhotos (flattened URLs)
          // but we need them in completion.beforePhotos/afterPhotos for the UI
          let beforePhotos: string[] = [];
          let afterPhotos: string[] = [];
          
          // Photos might be at jobData level (flattened) or in completion object
          if (jobData.beforePhotos && Array.isArray(jobData.beforePhotos)) {
            beforePhotos = jobData.beforePhotos;
          } else if (jobData.completion?.beforePhotos) {
            // If in completion, extract URLs if they're objects
            beforePhotos = jobData.completion.beforePhotos.map((photo: any) => 
              typeof photo === 'string' ? photo : photo.url
            );
          }
          
          if (jobData.afterPhotos && Array.isArray(jobData.afterPhotos)) {
            afterPhotos = jobData.afterPhotos;
          } else if (jobData.completion?.afterPhotos) {
            // If in completion, extract URLs if they're objects
            afterPhotos = jobData.completion.afterPhotos.map((photo: any) => 
              typeof photo === 'string' ? photo : photo.url
            );
          }
          
          const completionData = jobData.completion ? {
            ...jobData.completion,
            beforePhotos,
            afterPhotos,
          } : null;
          
          setCompletion(completionData);
        } else {
          setError('Job not found');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      setActionLoading(true);
      setError('');

      // Get current location
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser');
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const response = await apiService.post(`/crew/jobs/${jobId}/checkin`, {
            location: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            },
            crewSize,
          });

          if (response.success) {
            setCompletion(response.data as any);
            setCheckInDialog(false);
            await fetchJobDetails();
          } else {
            setError('Failed to check in');
          }
          setActionLoading(false);
        },
        (err) => {
          setError('Could not get your location. Please enable location services.');
          setActionLoading(false);
        }
      );
    } catch (err: any) {
      setError(err.message || 'Failed to check in');
      setActionLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Image file must be smaller than 10MB');
        return;
      }
      
      setPhotoFile(file);
      setPhotoUrl(''); // Clear URL if file is selected
      setError(''); // Clear any previous errors
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.onerror = () => {
        setError('Failed to read image file. Please try again.');
        setPhotoFile(null);
        setPhotoPreview('');
      };
      reader.readAsDataURL(file);
    } else {
      // Clear file if user cancels file selection
      setPhotoFile(null);
      setPhotoPreview('');
    }
  };

  const handleUploadPhoto = async () => {
    let finalPhotoUrl = photoUrl.trim();
    
    // If file is selected, convert to base64 data URL
    if (photoFile) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64String = e.target?.result as string;
          setActionLoading(true);
          setError('');

          // Photo uploads can take longer (base64 conversion, S3 upload, MongoDB save)
          // Use 60 second timeout for photo uploads (default is 10 seconds)
          const response = await apiService.post(`/crew/jobs/${jobId}/photos`, {
            photoType,
            photoUrl: base64String, // Send base64 data URL
          }, {
            timeout: 60000 // 60 seconds for photo uploads
          });

          if (response.success) {
            // The response.data is a flattened completion object with beforePhotos/afterPhotos as arrays
            const responseData = response.data as any;
            const completionData = {
              ...(responseData || {}),
              beforePhotos: responseData?.beforePhotos || [],
              afterPhotos: responseData?.afterPhotos || [],
            };
            // Immediately update completion state with new photos from upload response
            // The backend returns the updated completion with all photos, so we can trust this
            setCompletion(completionData as any);
            setPhotoFile(null);
            setPhotoPreview('');
            setPhotoUrl('');
            setError(''); // Clear any previous errors
            setPhotoDialog(false);
            // Don't refetch immediately - we already have the updated data from the upload response
            // Only refetch if user navigates away and comes back, or on page refresh
          } else {
            const errorMsg = response.error || response.message || 'Failed to upload photo';
            setError(errorMsg);
            console.error('[CREW-PHOTO-UPLOAD] Upload failed:', response);
          }
        } catch (err: any) {
          // Check if it's a timeout error
          const isTimeout = err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.isTimeout;
          let errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to upload photo';
          
          if (isTimeout) {
            errorMsg = 'Photo upload timed out. The photo may be too large or your connection is slow. Please try again with a smaller photo or check your internet connection.';
          }
          
          setError(errorMsg);
          console.error('[CREW-PHOTO-UPLOAD] Upload exception:', err);
        } finally {
          setActionLoading(false);
        }
      };
      reader.readAsDataURL(photoFile);
      return;
    }
    
    // Fallback to URL if no file
    if (!finalPhotoUrl) {
      setError('Please select a photo or enter a photo URL');
      return;
    }

    try {
      setActionLoading(true);
      setError('');

      const response = await apiService.post(`/crew/jobs/${jobId}/photos`, {
        photoType,
        photoUrl: finalPhotoUrl,
      });

      if (response.success) {
        // The response.data is a flattened completion object with beforePhotos/afterPhotos as arrays
        const responseData = response.data as any;
        const completionData = {
          ...(responseData || {}),
          beforePhotos: responseData?.beforePhotos || [],
          afterPhotos: responseData?.afterPhotos || [],
        };
        // Immediately update completion state with new photos from upload response
        // The backend returns the updated completion with all photos, so we can trust this
        setCompletion(completionData as any);
        setPhotoUrl('');
        setPhotoDialog(false);
        // Don't refetch immediately - we already have the updated data from the upload response
        // Only refetch if user navigates away and comes back, or on page refresh
      } else {
        setError('Failed to upload photo');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload photo');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      setActionLoading(true);
      setError('');

      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported');
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            // Use a longer timeout (30 seconds) for job completion since payment processing can take time
            const response = await apiService.post(`/crew/jobs/${jobId}/complete`, {
              location: {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              },
              notes: notes.trim(),
              issues: issues.trim() ? issues.split(',').map(i => i.trim()) : [],
            }, { timeout: 30000 }) as any; // Response includes payment and error fields not in ApiResponse type

            if (response.success) {
              // Check if payment processing failed
              if (response.paymentError || response.error) {
                const errorMessage = response.paymentError?.reason || response.error?.reason || response.paymentError || response.error || 'Payment processing failed';
                setError(`Job completed but payment failed: ${errorMessage}. Please contact support or retry payment.`);
                setActionLoading(false);
                return;
              }

              // Check payment status
              if (response.payment && response.payment.status !== 'completed' && response.payment.status !== 'skip') {
                const statusMessage = response.payment.status === 'failed' 
                  ? `Payment failed: ${response.payment.failureReason || 'Unknown error'}`
                  : 'Payment is pending and will process later';
                setError(`Job completed but ${statusMessage}. Please contact support if needed.`);
                setActionLoading(false);
                return;
              }

              // Success - payment processed or skipped
              setCompleteDialog(false);
              // Refresh job details to update completion status
              await fetchJobDetails();
              const paymentMessage = response.payment?.status === 'skip' 
                ? 'Job completed successfully! No payment required.'
                : 'Job completed successfully! Payment has been processed.';
              alert(paymentMessage);
              // Navigate back to dashboard - it will refresh and show job as completed
              navigate('/crew/dashboard');
            } else {
              setError(response.error || response.message || 'Failed to complete job');
            }
          } catch (err: any) {
            // Handle network errors and API errors
            const errorMessage = err.message || 'Failed to complete job';
            if (errorMessage.includes('Network error') || errorMessage.includes('timeout')) {
              setError('Job completion is taking longer than expected. The job may have been completed, but payment processing may still be in progress. Please check the job status or contact support.');
            } else {
              setError(errorMessage);
            }
          } finally {
            setActionLoading(false);
          }
        },
        (err) => {
          setError('Could not get your location');
          setActionLoading(false);
        }
      );
    } catch (err: any) {
      setError(err.message || 'Failed to complete job');
      setActionLoading(false);
    }
  };

  const openDirections = () => {
    if (job) {
      // Use coordinates if available (more accurate), otherwise use address
      let destination = '';
      if (job.coordinates && job.coordinates.lat && job.coordinates.lng) {
        // Use coordinates for more accurate navigation
        destination = `${job.coordinates.lat},${job.coordinates.lng}`;
      } else {
        // Fallback to address
        const address = `${job.address.street}, ${job.address.city}, ${job.address.state} ${job.address.zipCode}`;
        destination = encodeURIComponent(address);
      }
      
      // Google Maps URL – dir_action=navigate starts turn-by-turn immediately
      // when origin defaults to current location (no address input/overview)
      const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=current+location&destination=${destination}&travelmode=driving&dir_action=navigate`;
      window.open(mapsUrl, '_blank');
    }
  };

  const callCustomer = () => {
    const customer = job?.customerId;
    if (customer?.phone) {
      window.location.href = `tel:${customer.phone}`;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!job) {
    return (
      <Container>
        <Alert severity="error">{error || 'Job not found'}</Alert>
      </Container>
    );
  }

  const customer = job.customerId;
  const isCompleted = completion?.status === 'completed';
  const isCheckedIn = completion && completion.status !== 'completed';

  return (
    <Box sx={{ pb: 4, bgcolor: 'grey.50', minHeight: '100vh' }}>
      {/* Mobile Header */}
      <AppBar position="sticky">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/crew/dashboard')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 2 }}>
            Job Details
          </Typography>
          {completion && (
            <Chip
              label={completion.status === 'completed' ? 'Completed' : 'In Progress'}
              color={completion.status === 'completed' ? 'success' : 'warning'}
              size="small"
              sx={{ color: 'white' }}
            />
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Service Info */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {typeof job.serviceType === 'string' ? job.serviceType : job.serviceType?.name}
              </Typography>
              {job.baggingRequired && (
                <Chip 
                  label="Bagging Required" 
                  color="warning" 
                  size="medium"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" paragraph>
              {typeof job.serviceType === 'object' && job.serviceType?.description}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Address */}
            <Stack direction="row" spacing={1} alignItems="flex-start" mb={2}>
              <LocationOn color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {job.address.street}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {job.address.city}, {job.address.state} {job.address.zipCode}
                </Typography>
              </Box>
            </Stack>

            {/* Schedule */}
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <Schedule color="action" />
              <Typography variant="body2">
                Scheduled: {job.scheduledTime || 'Not specified'}
              </Typography>
            </Stack>

            {/* Customer Info */}
            {customer && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Phone color="action" />
                <Typography variant="body2">
                  {customer.firstName} {customer.lastName}
                  {customer.phone && ` • ${customer.phone}`}
                </Typography>
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DirectionsCar />}
            onClick={openDirections}
            fullWidth
          >
            Directions
          </Button>
          {customer?.phone && (
            <Button
              variant="outlined"
              startIcon={<Phone />}
              onClick={callCustomer}
              fullWidth
            >
              Call
            </Button>
          )}
        </Stack>

        {/* Photos Section */}
        {isCheckedIn && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Photos</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CameraAlt />}
                  onClick={() => {
                    setPhotoType(completion?.status === 'checked_in' ? 'before' : 'after');
                    setPhotoDialog(true);
                  }}
                  disabled={isCompleted}
                >
                  Add Photo
                </Button>
              </Stack>

              {completion?.beforePhotos && completion.beforePhotos.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Before Photos</Typography>
                  <ImageList cols={3} gap={8}>
                    {completion.beforePhotos.map((url: string, index: number) => (
                      <PhotoItem key={`before-${url}-${index}`} url={url} alt={`Before ${index + 1}`} />
                    ))}
                  </ImageList>
                </Box>
              )}

              {completion?.afterPhotos && completion.afterPhotos.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>After Photos</Typography>
                  <ImageList cols={3} gap={8}>
                    {completion.afterPhotos.map((url: string, index: number) => (
                      <PhotoItem key={`after-${url}-${index}`} url={url} alt={`After ${index + 1}`} />
                    ))}
                  </ImageList>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Special Instructions */}
        {job.specialInstructions && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} mb={1}>
                <Assignment color="primary" />
                <Typography variant="h6">Special Instructions</Typography>
              </Stack>
              <Typography variant="body2">{job.specialInstructions}</Typography>
            </CardContent>
          </Card>
        )}

        {/* Main Action Button */}
        {!isCompleted && (
          <>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => {
                if (!isCheckedIn) {
                  setCheckInDialog(true);
                } else {
                  setCompleteDialog(true);
                }
              }}
              startIcon={isCheckedIn ? <CheckCircle /> : <LocationOn />}
              sx={{ py: 2, fontSize: '1.1rem' }}
            >
              {isCheckedIn ? 'Complete Job' : 'Check In & Start'}
            </Button>
            {(job as any).frequency !== 'per-instance' &&
              (job as any).serviceType?.category !== 'snow_removal' && (
                <Button
                  variant="outlined"
                  color="inherit"
                  size="large"
                  fullWidth
                  startIcon={<SkipNext />}
                  sx={{ mt: 1, py: 1.5 }}
                  onClick={async () => {
                    if (
                      !window.confirm(
                        'Skip this visit? Customer will not be charged. Next recurring service date will be set and the customer notified.'
                      )
                    ) {
                      return;
                    }
                    try {
                      const res = await apiService.post(`/crew/jobs/${jobId}/skip`, {});
                      if (!res.success) {
                        alert(res.error || 'Failed to skip visit');
                        return;
                      }
                      alert((res as any).message || 'Visit skipped.');
                      navigate('/crew');
                    } catch (e: any) {
                      alert(e.message || 'Failed to skip visit');
                    }
                  }}
                >
                  Skip Visit
                </Button>
              )}
          </>
        )}

        {isCompleted && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="h6">Job Completed!</Typography>
            <Typography variant="body2">
              Payment has been processed automatically.
              {completion?.actualDuration && ` Duration: ${completion.actualDuration.toFixed(2)} hours`}
            </Typography>
          </Alert>
        )}
      </Container>

      {/* Check-in Dialog */}
      <Dialog open={checkInDialog} onClose={() => !actionLoading && setCheckInDialog(false)}>
        <DialogTitle>Check In to Job</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            This will record your location and start time.
          </Typography>
          <TextField
            label="Crew Size"
            type="number"
            fullWidth
            value={crewSize}
            onChange={(e) => setCrewSize(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1 }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckInDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCheckIn} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Check In'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Upload Dialog */}
      <Dialog open={photoDialog} onClose={() => !actionLoading && setPhotoDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add {photoType === 'before' ? 'Before' : 'After'} Photo</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ mb: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="photo-file-input"
              type="file"
              capture="environment" // Use back camera on mobile
              onChange={handleFileChange}
            />
            <label htmlFor="photo-file-input">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<CameraAlt />}
                sx={{ mb: 2 }}
              >
                {photoFile ? 'Change Photo' : 'Take Photo or Choose File'}
              </Button>
            </label>
            
            {photoPreview && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <img
                  src={photoPreview}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: '8px',
                  }}
                />
              </Box>
            )}
          </Box>
          
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
            OR
          </Typography>
          
          <TextField
            label="Photo URL"
            fullWidth
            value={photoUrl}
            onChange={(e) => {
              setPhotoUrl(e.target.value);
              setPhotoFile(null); // Clear file if URL is entered
              setPhotoPreview('');
              setError(''); // Clear error when user types
            }}
            placeholder="https://example.com/photo.jpg"
            disabled={!!photoFile}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setPhotoDialog(false);
              setPhotoFile(null);
              setPhotoPreview('');
              setPhotoUrl('');
              setError(''); // Clear error when closing dialog
            }} 
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUploadPhoto} disabled={actionLoading || (!photoFile && !photoUrl.trim())}>
            {actionLoading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Job Dialog */}
      <Dialog 
        open={completeDialog} 
        onClose={() => !actionLoading && setCompleteDialog(false)}
        fullWidth
      >
        <DialogTitle>Complete Job</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            This will mark the job as complete and automatically process payment.
          </Typography>
          <TextField
            label="Completion Notes"
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any important notes about the work completed..."
            sx={{ mb: 2 }}
          />
          <TextField
            label="Issues (comma-separated)"
            fullWidth
            value={issues}
            onChange={(e) => setIssues(e.target.value)}
            placeholder="e.g., sprinkler broken, gate locked"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            onClick={handleComplete} 
            disabled={actionLoading}
            startIcon={<CheckCircle />}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Complete Job'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrewJobDetailsPage;

